import { useContext, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Avatar,
  Icon,
  Flex,
  Spacer,
  Circle,
  Divider,
  Spinner,
  Button,
  useDisclosure,
} from '@chakra-ui/react';

import PageContainer from '~/containers/PageContainer';
import SectionContainer from '~/containers/SectionContainer';
import Content from '~/containers/Content';

import CouponItem from '~/components/CouponItem';

import authAtom from '~/recoil/atom/auth';
import { useRecoilValue } from 'recoil';

import { useNavigate, useParams } from 'react-router-dom';

import useCoupons from './hooks/useCoupons';

import { DisplayStoresContext } from '~/provider/DisplayStoresProvider';

import * as fp from 'lodash/fp';

import { Coupon } from '~/icons';
import {
  IoCalendarOutline,
  IoCreateOutline,
  IoMailSharp,
  IoPersonCircleOutline,
  IoPhonePortraitOutline,
  IoWallet,
} from 'react-icons/io5';
import { FetchingDataStatus } from '~/types/utilities';
import { BtnVariant } from '~/theme/components/Button';
import { MemberInfoModal } from './components';
import customer from '~/recoil/atom/customer';

export default function MemberCenter() {
  const auth = useRecoilValue(authAtom);
  const customerData = useRecoilValue(customer);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const navigate = useNavigate();
  const { groupId = '' } = useParams();

  const { stores } = useContext(DisplayStoresContext);

  console.log(customerData);
  const { coupons, couponFetchingStatus } = useCoupons(auth);

  const storeNameMap = useMemo(() => {
    return new Map(stores.map(({ name, objectID }) => [objectID, name]));
  }, [stores]);

  const filteredCoupons = useMemo(() => {
    return coupons.filter(fp.prop('allowDisplay'));
  }, [coupons]);

  return (
    <>
      <PageContainer topBoxStyle={{ position: 'static' }}>
        <Box p={4}>
          <Content>
            <VStack align="stretch" spacing={4}>
              <SectionContainer
                title="會員中心"
                icon={<Icon as={IoPersonCircleOutline} color="secondary" boxSize="1.5rem" />}
              ></SectionContainer>

              <VStack
                spacing="8px"
                p="16px"
                borderRadius="5px"
                border="1px solid"
                borderColor="primaryLight"
              >
                <Flex justify="flex-start" w="full" fontSize="14px" fontWeight={700}>
                  <Text fontSize="sm">{customerData?.displayName || '-'}</Text>
                </Flex>
                <Divider borderColor="primaryLight" />

                <Flex justify="space-between" w="full">
                  <HStack>
                    <Icon boxSize="1rem" as={IoPhonePortraitOutline} />
                    <Text fontSize="sm">手機</Text>
                  </HStack>
                  <Text fontSize="sm">{customerData?.phoneNumber || '-'}</Text>
                </Flex>

                <Flex justify="space-between" w="full">
                  <HStack>
                    <Icon boxSize="1rem" as={IoCalendarOutline} />
                    <Text fontSize="sm">出生日期</Text>
                  </HStack>
                  <Text fontSize="sm">{customerData?.birthday || '-'}</Text>
                </Flex>

                <Flex justify="space-between" w="full">
                  <HStack>
                    <Icon boxSize="1rem" as={IoMailSharp} />
                    <Text fontSize="sm">電子信箱</Text>
                  </HStack>
                  <Text fontSize="sm">{customerData?.email || '-'}</Text>
                </Flex>

                <Button
                  variant={BtnVariant.SOLID_LIGHT}
                  fontWeight="normal"
                  w="full"
                  rightIcon={<Icon as={IoCreateOutline} boxSize="1.25rem" />}
                  onClick={onOpen}
                >
                  編輯個人資訊
                </Button>
              </VStack>

              <VStack spacing={4} align="stretch">
                <Flex bgColor="secondary" borderRadius="5px" p={2}>
                  <HStack>
                    <Circle bgColor="white" p={1}>
                      <Icon as={IoWallet} color="primary" boxSize="1.25rem" />
                    </Circle>
                    <Text color="white">目前已賺優惠</Text>
                  </HStack>
                  <Spacer />
                  <Text fontWeight="bold" color="white">
                    NT$ 0
                  </Text>
                </Flex>
              </VStack>

              <SectionContainer
                title="優惠券"
                icon={<Icon as={Coupon} color="secondary" boxSize="1.25rem" />}
                rightHandComponent={
                  <>
                    {fp.gt(filteredCoupons.length, 0) && (
                      <>
                        <Circle bgColor="red" w="1rem" h="1rem">
                          <Text color="white" fontSize="xs">
                            {filteredCoupons.length}
                          </Text>
                        </Circle>
                        <Spacer />
                      </>
                    )}
                  </>
                }
              >
                <Divider borderColor="primary" borderWidth="1px" />
                {fp.cond([
                  [
                    fp.eq(FetchingDataStatus.LOADING),
                    fp.always(
                      <Flex justify="center" py={6}>
                        <Spinner size="lg" />
                      </Flex>,
                    ),
                  ],
                  [
                    fp.T,
                    fp.always(
                      <VStack py={4} align="stretch">
                        {filteredCoupons.map((coupon) => (
                          <CouponItem
                            coupon={{
                              ...coupon,
                              storesName: coupon.stores
                                .map((id) => storeNameMap.get(id) || '')
                                .filter((name) => !!name),
                            }}
                            key={coupon.id}
                            onViewDetail={() => navigate(`${coupon.id}`)}
                            onNavigate={() =>
                              navigate(groupId ? `/group/${groupId}/filterMeal` : `/filterMeal`)
                            }
                          />
                        ))}
                      </VStack>,
                    ),
                  ],
                ])(couponFetchingStatus)}
              </SectionContainer>
            </VStack>
          </Content>
        </Box>
      </PageContainer>
      <MemberInfoModal
        isOpen={isOpen}
        onClose={onClose}
        defaultValues={{
          id: customerData?.id || '',
          displayName: customerData?.displayName || '',
          phoneNumber: customerData?.phoneNumber || '',
          birthday: customerData?.birthday || '',
          email: customerData?.email || '',
        }}
      />
    </>
  );
}
